import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";

const DEBOUNCE_DELAY = 500;

export const usePayout = () => {
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [metrics, setMetrics] = useState({});

  const [balanceDetails, setBalanceDetails] = useState({});

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    settlement_window: "",
    order_id: "",
    order_settlement_status: "",
    buyer_app_id: "",
    search_by: "",
    search_value: "",
  });

  const [ledger, setLedger] = useState([]);
  const debounceTimer = useRef(null);

  const getPayouts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      const filterKeys = [
        "start_date",
        "end_date",
        "settlement_window",
        "order_id",
        "order_settlement_status",
        "buyer_app_id",
      ];
      filterKeys.forEach((key) => {
        if (filters[key]) params.append(key, filters[key]);
      });

      if (filters.search_by && filters.search_value) {
        params.append(filters.search_by, filters.search_value.trim());
      }

      params.append("page", currentPage);
      params.append("limit", itemsPerPage);

      const result = await apiHandler({
        url: `${endpoint.GET_PAYOUTS}?${params.toString()}`,
        method: "GET",
        headers: { "Content-Type": "application/json", tenantid: storeId },
        authToken: authData,
        dispatch,
        storeId,
      });

      const apiRes = result?.data?.data;
      if (apiRes) {
        setData(apiRes.buyer_wise || []);
        // Match the pagination count key from your API structure
        setTotalOrderCount(
          result?.data?.pagination?.count || apiRes.buyer_wise?.length || 0,
        );

        if (apiRes.cards) {
          const card = apiRes.cards;
          const toNum = (v) => {
            if (v === null || v === undefined || v === "") return 0;
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
          };

          // Dynamically map all fields from API response
          const metricsData = {};
          Object.keys(card).forEach((key) => {
            metricsData[key] = toNum(card[key]);
          });
          setMetrics(metricsData);
        } else {
          setMetrics({});
        }
        setLedger(apiRes.balance_sheet || []);
        setBalanceDetails(apiRes.balance_sheet_details || {});
      }
    } catch (err) {
      //console.error("Payout API Error:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage, authData, dispatch, storeId]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const isDebounced =
      filters.search_value &&
      ["order_id", "buyer_app_id"].includes(filters.search_by);
    debounceTimer.current = setTimeout(
      () => getPayouts(),
      isDebounced ? DEBOUNCE_DELAY : 0,
    );
    return () => clearTimeout(debounceTimer.current);
  }, [filters, getPayouts]);

  const totalPages = Math.ceil(totalOrderCount / itemsPerPage);
  const startItem =
    totalOrderCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalOrderCount);

  const goToPage = (page) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(Number(newItemsPerPage));
    setCurrentPage(1);
  };

  const paginatedPayouts = useMemo(() => data, [data]);

  return {
    paginatedPayouts,
    loading,
    totalOrderCount,
    filters,
    setFilters,
    metrics,
    ledger,
    balanceDetails,
    totalPages,
    currentPage,
    setCurrentPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    handleItemsPerPageChange,
  };
};
